import { Component, OnInit } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { computed, observable } from 'mobx-angular';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  sub,
} from 'date-fns';
import { UserService } from '../../../services';

import {
  EngineiusHomepageLinkDTO,
  EngineiusHomepageService,
  KeyMetricType,
  KeyMetricValue,
} from '../../../services';

type metricPeriod = 'Day' | 'Week' | 'Month' | 'Year';

export class KeyMetric {
  title: string;
  previousPeriod: KeyMetricValue = new KeyMetricValue();
  currentPeriod: KeyMetricValue = new KeyMetricValue();
  nextPeriod: KeyMetricValue = new KeyMetricValue();
  postUnit: string;
  preUnit: string;
  metricType: KeyMetricType;

  constructor(title, metricType, postUnit, preUnit) {
    this.title = title;
    this.metricType = metricType;
    this.postUnit = postUnit;
    this.preUnit = preUnit;
  }
}

interface MetricRangeOption {
  label: string;
  startDate: Date;
  endDate: Date;
}

const allMetricRangeOptions: Record<metricPeriod, MetricRangeOption[]> = {
  Day: Array.from(Array(7).keys()).map((i) => {
    let date = sub(new Date(), { days: i });
    return {
      label: format(date, 'PPPP'),
      startDate: startOfDay(date),
      endDate: endOfDay(date),
    };
  }),
  Month: Array.from(Array(7).keys()).map((i) => {
    let date = sub(new Date(), { months: i });
    return {
      label: format(date, 'MMMM yyyy'),
      startDate: startOfMonth(date),
      endDate: endOfMonth(date),
    };
  }),
  Week: Array.from(Array(7).keys()).map((i) => {
    let date = sub(new Date(), { weeks: i });
    let sow = startOfWeek(date, { weekStartsOn: 1 });
    let eow = endOfWeek(date, { weekStartsOn: 1 });
    return {
      label: `${format(sow, 'dd MMMM yy')} - ${format(eow, 'dd MMMM yy')}`,
      startDate: sow,
      endDate: eow,
    };
  }),
  Year: Array.from(Array(3).keys()).map((i) => {
    let date = sub(new Date(), { years: i });
    let soy = startOfYear(date);
    let eoy = endOfYear(date);
    return {
      label: format(soy, 'yyyy'),
      startDate: soy,
      endDate: eoy,
    };
  }),
};

const defaultMetrics: KeyMetric[] = [
  new KeyMetric('Movements Completed', KeyMetricType.MovementsCompleted, '', ''),
  new KeyMetric('Movements On Time', KeyMetricType.PercentageOnTime, '%', ''),
  new KeyMetric('Auto Deployed', KeyMetricType.PercentageAutoDeployed, '%', ''),
  new KeyMetric('GPAI / Mvmt', KeyMetricType.GrossProfitAfterInsurance, '', '£'),
];

@Component({
  selector: 'engineius-homepage',
  templateUrl: './engineius-homepagecomponent.html',
  styleUrls: ['./engineius-homepage.component.scss'],
})
export class EngineiusHomepageComponent implements OnInit {
  @observable keyMetrics: KeyMetric[] = defaultMetrics;

  public metricsLoading: boolean = false;

  public mainLink: EngineiusHomepageLinkDTO;

  public secondaryLinks: EngineiusHomepageLinkDTO[] = [];

  public metricPeriodOptions: SelectItem[] = [
    { label: 'Day', value: 'Day' },
    { label: 'Week', value: 'Week' },
    { label: 'Month', value: 'Month' },
    { label: 'Year', value: 'Year' },
  ];

  public get user() {
    return this.userService.currentUser;
  }

  @observable pinnedMetricType: KeyMetricType = KeyMetricType.MovementsCompleted;

  @observable activePeriod: metricPeriod = 'Month';

  @computed get metricRangeOptions() {
    return allMetricRangeOptions[this.activePeriod];
  }

  @computed get sortedKeyMetrics() {
    return [...this.keyMetrics].sort(
      (a, b) =>
        Number(b.metricType == this.pinnedMetricType) -
        Number(a.metricType == this.pinnedMetricType),
    );
  }

  public activeRangeOption: MetricRangeOption = allMetricRangeOptions[this.activePeriod][0];

  constructor(
    public userService: UserService,
    private engineiusHomepageService: EngineiusHomepageService,
  ) {}

  ngOnInit() {
    void this.refreshKeyMetrics();
    void this.getLinks();
    this.pinnedMetricType = Number(localStorage.getItem('pinned-key-metric'));
  }

  onActivePeriodChange() {
    this.activeRangeOption = allMetricRangeOptions[this.activePeriod][0];
    void this.refreshKeyMetrics();
  }

  pinMetric(metricType: KeyMetricType) {
    this.pinnedMetricType = metricType;
    localStorage.setItem('pinned-key-metric', metricType.toString());
  }

  async refreshKeyMetrics() {
    try {
      this.metricsLoading = true;
      let keyMetricResult = await this.engineiusHomepageService.getKeyMetrics(
        this.activeRangeOption.startDate,
        this.activeRangeOption.endDate,
      );
      this.keyMetrics = defaultMetrics.map((value) => ({
        ...value,
        currentPeriod: keyMetricResult[KeyMetricType[value.metricType]].currentPeriod,
        nextPeriod: keyMetricResult[KeyMetricType[value.metricType]].nextPeriod,
        previousPeriod: keyMetricResult[KeyMetricType[value.metricType]].previousPeriod,
      }));
    } catch (e) {
      this.keyMetrics = defaultMetrics;
    } finally {
      this.metricsLoading = false;
    }
  }

  async getLinks() {
    let allLinks = await this.engineiusHomepageService.getWelcomeLinks();
    if (allLinks.length > 0) {
      this.mainLink = allLinks.shift();
      this.secondaryLinks = allLinks;
    }
  }
}
